/**
 * Создание доски. Макет data/Задачник.dc.html, префикс cb* (25 плейсхолдеров).
 *
 * §8.2 и §8.3: режим привязки проекта выбирается ПРИ СОЗДАНИИ и определяет,
 * что заполняется дальше — проект доски или проект каждого столбца.
 * Инвариант §20.3 не допускает заполнения обоих сразу, поэтому форма
 * показывает ровно одно из двух, а не оба поля с подсказкой.
 *
 * §2.2 задаёт значения по умолчанию: ОА и ООА — BOARD, ОАП — COLUMN.
 * Это умолчание, а не запрет: другой режим разрешён явным выбором.
 *
 * Третий вариант — «Личная (без проекта)»: режим BOARD без проекта. Её
 * видит только создатель, участников в ней нет, в отчётность она не идёт.
 * Её может завести любой сотрудник; без права board.create это
 * единственный доступный вариант.
 */

import { errorMessage, newRequestId, usePermission } from "@taskflow/client-core";
import { DepartmentCode, Permission, ProjectBindingMode } from "@taskflow/contracts";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAppSelector } from "@/app/hooks";
import {
  departmentOptionLabel,
  departmentTree,
  useDepartmentsQuery,
  useUserDirectoryQuery,
} from "@taskflow/client-core/directory";
import {
  Button,
  FormField,
  IconButton,
  Input,
  Modal,
  MultiSelectPicker,
  type PickerOption,
  SegmentedControl,
  Select,
  Textarea,
  toast,
} from "@/shared/ui";
import { Plus, Trash2 } from "@/shared/ui/motionIcons";

import { useProjectsQuery } from "@taskflow/client-core/admin";
import { useCreateBoardMutation } from "@taskflow/client-core/boards";
import { rememberBoardId } from "./lastBoard";
import { STATUS_CATEGORIES } from "./statusCategories";

interface DraftColumn {
  localId: string;
  name: string;
  projectId: string | null;
  /** Статус для отчётности; нужен только в режиме «один проект на всю доску». */
  statusCategory: string | null;
}

/** Выбор в форме: режим привязки или личная доска (на сервере — BOARD). */
const PERSONAL = "PERSONAL";
type BindingChoice = ProjectBindingMode | typeof PERSONAL;

/**
 * §2.2: режим по умолчанию зависит от подразделения — по его КОДУ.
 * Прежде сравнивался выдуманный идентификатор «dep-oap», которого
 * у настоящих подразделений нет, и умолчание ОАП не срабатывало никогда.
 */
function defaultMode(code: string | undefined): ProjectBindingMode {
  return code === DepartmentCode.OAP ? ProjectBindingMode.COLUMN : ProjectBindingMode.BOARD;
}

function defaultColumns(mode: BindingChoice): DraftColumn[] {
  return mode === ProjectBindingMode.COLUMN
    ? [{ localId: "c1", name: "", projectId: null, statusCategory: null }]
    : [
        { localId: "c1", name: "Открыто", projectId: null, statusCategory: "OPEN" },
        { localId: "c2", name: "В работе", projectId: null, statusCategory: "IN_PROGRESS" },
        { localId: "c3", name: "Готово", projectId: null, statusCategory: "DONE" },
      ];
}

export function CreateBoardDialog({
  open,
  onClose,
  initialDepartmentId,
}: {
  open: boolean;
  onClose: () => void;
  /** Подразделение открытой вкладки; по умолчанию — основное пользователя. */
  initialDepartmentId?: string;
}) {
  const navigate = useNavigate();
  const user = useAppSelector((state) => state.auth.user);
  const [create, { isLoading }] = useCreateBoardMutation();
  // Без права на создание досок — только личная.
  const canCreateShared = usePermission(Permission.BOARD_CREATE);
  const modeFor = (code: string | undefined): BindingChoice =>
    canCreateShared ? defaultMode(code) : PERSONAL;

  // Справочники — с сервера. Прежде здесь стояли константы с выдуманными
  // идентификаторами, и сервер отклонял создание любой доски.
  const departmentsQuery = useDepartmentsQuery();
  const projectsQuery = useProjectsQuery({});
  const directory = useUserDirectoryQuery({}, { skip: !open });

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [departmentId, setDepartmentId] = useState(
    initialDepartmentId ?? user?.primaryDepartmentId ?? "",
  );
  const [mode, setMode] = useState<BindingChoice>(
    canCreateShared ? ProjectBindingMode.BOARD : PERSONAL,
  );
  const [projectId, setProjectId] = useState<string | null>(null);
  const [columns, setColumns] = useState<DraftColumn[]>(() =>
    defaultColumns(ProjectBindingMode.BOARD),
  );
  const [memberIds, setMemberIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const departments = departmentsQuery.data?.data ?? [];
  const codeOf = (id: string) => departments.find((item) => item.id === id)?.code;

  // Умолчание §2.2 применяется, когда подразделения загрузились, — один раз
  // на подразделение. Подстройка во время рендера, а не в эффекте: так
  // форма не мигает режимом BOARD перед переключением на COLUMN.
  const [defaultsFor, setDefaultsFor] = useState<string | null>(null);
  if (departments.length > 0 && departmentId !== "" && defaultsFor !== departmentId) {
    const next = modeFor(codeOf(departmentId));
    setDefaultsFor(departmentId);
    setMode(next);
    setColumns(defaultColumns(next));
    setProjectId(null);
  }

  // Подразделения ограничены областью доступа пользователя (§2.3).
  // Порядок дерева: вышестоящее, за ним подчинённые с отступом.
  const allowedDepartments: PickerOption[] = departmentTree(departments)
    .filter(
      (item) =>
        item.id === user?.primaryDepartmentId ||
        (user?.allowedDepartmentIds.includes(item.id) ?? false),
    )
    .map((item) => ({ id: item.id, label: departmentOptionLabel(item) }));

  // Архивный проект новой доске не предлагается (§7.1).
  const projects: PickerOption[] = (projectsQuery.data?.data ?? [])
    .filter((item) => item.status === "ACTIVE")
    .map((item) =>
      item.code ? { id: item.id, label: item.name, hint: item.code } : { id: item.id, label: item.name },
    );

  const candidates: PickerOption[] = (directory.data?.data ?? [])
    .filter((person) => person.id !== user?.id)
    .map((person) => ({
      id: person.id,
      label: `${person.lastName} ${person.firstName}`,
      hint: person.position ?? person.departmentName,
    }));

  const changeDepartment = (id: string) => {
    setDepartmentId(id);
    const next = modeFor(codeOf(id));
    setDefaultsFor(id);
    setMode(next);
    setColumns(defaultColumns(next));
    setProjectId(null);
  };

  const changeMode = (next: BindingChoice) => {
    setMode(next);
    setColumns(defaultColumns(next));
    setProjectId(null);
  };

  const columnMode = mode === ProjectBindingMode.COLUMN;
  const personal = mode === PERSONAL;

  const validate = (): string | null => {
    if (name.trim().length === 0) return "Укажите название доски";
    if (departmentId === "") return "Выберите подразделение";
    if (columns.length === 0) return "Нужен хотя бы один столбец";
    if (columns.some((column) => column.name.trim().length === 0)) {
      return "У всех столбцов должно быть название";
    }
    // Инвариант §20.3 проверяется до отправки, чтобы не ловить 422.
    if (columnMode && columns.some((column) => column.projectId === null)) {
      return "В режиме «проект для каждого столбца» проект обязателен для каждого столбца";
    }
    // Без статуса задачи столбца выпадают из отчётности (§17.4).
    if (!columnMode && columns.some((column) => column.statusCategory === null)) {
      return "Выберите статус для каждого столбца";
    }
    if (!columnMode && !personal && projectId === null) {
      return "В режиме «один проект на всю доску» выберите проект";
    }
    return null;
  };

  const submit = async () => {
    const problem = validate();
    if (problem !== null) {
      setError(problem);
      return;
    }
    setError(null);

    try {
      const board = await create({
        name: name.trim(),
        description: description.trim() === "" ? null : description.trim(),
        departmentId,
        projectBindingMode: personal ? ProjectBindingMode.BOARD : mode,
        projectId: columnMode || personal ? null : projectId,
        columns: columns.map((column) => ({
          name: column.name.trim(),
          projectId: columnMode ? column.projectId : null,
          statusCategory: columnMode ? null : column.statusCategory,
        })),
        memberIds: personal ? [] : memberIds,
        ...(personal ? { personal: true } : {}),
        // §22.3: повторная отправка не создаст вторую доску.
        clientRequestId: newRequestId(),
      }).unwrap();

      rememberBoardId(board.id, board.departmentCode);
      toast.success("Доска создана");
      onClose();
      void navigate(`/boards/${board.id}`);
    } catch (raw) {
      setError(errorMessage(raw));
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Создание доски"
      width="lg"
      dismissable={!isLoading}
      footer={
        <>
          {/* Сообщение — в подвале, рядом с кнопкой: прежде оно стояло под
              длинной формой, вне экрана, и «Создать» казалась нерабочей. */}
          {error !== null && (
            <p role="alert" className="mr-auto self-center text-[13px] font-semibold text-danger">
              {error}
            </p>
          )}
          <Button variant="secondary" onClick={onClose} disabled={isLoading}>
            Отмена
          </Button>
          <Button loading={isLoading} onClick={() => void submit()}>
            Создать
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3.5">
        <FormField label="Название доски" required>
          {(props) => (
            <Input {...props} value={name} onChange={(e) => setName(e.target.value)} autoFocus />
          )}
        </FormField>

        <FormField label="Описание">
          {(props) => (
            <Textarea
              {...props}
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          )}
        </FormField>

        <FormField label="Подразделение" required hint="Определяет область видимости задач">
          {(props) => (
            <Select
              {...props}
              label="Подразделение доски"
              placeholder="Выберите подразделение"
              options={allowedDepartments}
              value={departmentId}
              onChange={changeDepartment}
            />
          )}
        </FormField>

        <div className="flex flex-col gap-1.5">
          <span className="text-[13px] font-semibold text-muted">Способ привязки проекта</span>
          <SegmentedControl<BindingChoice>
            label="Способ привязки проекта"
            value={mode}
            options={[
              {
                value: ProjectBindingMode.BOARD,
                label: "Один проект на всю доску",
                disabled: !canCreateShared,
              },
              {
                value: ProjectBindingMode.COLUMN,
                label: "Проект для каждого столбца",
                disabled: !canCreateShared,
              },
              { value: PERSONAL, label: "Личная (без проекта)" },
            ]}
            onChange={changeMode}
          />
          {!canCreateShared && (
            <p className="text-xs text-faint">
              Для досок с проектом нужно право «Создание досок» — вам доступна личная доска.
            </p>
          )}
          {/* Пояснения — из макета. */}
          <p className="text-xs text-faint">
            {columnMode
              ? "Каждый столбец относится к своему проекту. Проект задачи определяется столбцом."
              : personal
                ? "Доску видите только вы, добавить в неё пользователей нельзя. Задачи не попадают в отчётность."
                : "Все задачи доски относятся к одному проекту. Столбцы — этапы или статусы работы."}
          </p>
        </div>

        {/* Показывается ровно одно из двух: инвариант §20.3 не допускает обоих. */}
        {!columnMode && !personal && (
          <FormField label="Проект доски" required>
            {(props) => (
              <Select
                {...props}
                label="Проект доски"
                placeholder="Выберите проект"
                options={projects}
                value={projectId}
                onChange={setProjectId}
              />
            )}
          </FormField>
        )}

        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1 text-[13px] font-semibold text-muted">
            Столбцы {columnMode ? "и их проекты" : "и их статусы"}
          </legend>

          {columns.map((column, index) => (
            <div key={column.localId} className="flex items-start gap-2">
              <Input
                value={column.name}
                aria-label={`Название столбца ${String(index + 1)}`}
                placeholder="Название столбца"
                onChange={(event) =>
                  setColumns((prev) =>
                    prev.map((item) =>
                      item.localId === column.localId
                        ? { ...item, name: event.target.value }
                        : item,
                    ),
                  )
                }
              />

              {!columnMode && (
                <div className="w-44 shrink-0">
                  <Select
                    label={`Статус столбца ${String(index + 1)}`}
                    placeholder="Статус"
                    options={STATUS_CATEGORIES}
                    value={column.statusCategory}
                    onChange={(value) =>
                      setColumns((prev) =>
                        prev.map((item) =>
                          item.localId === column.localId
                            ? { ...item, statusCategory: value }
                            : item,
                        ),
                      )
                    }
                  />
                </div>
              )}

              {columnMode && (
                <div className="w-56 shrink-0">
                  <Select
                    label={`Проект столбца ${String(index + 1)}`}
                    placeholder="Проект"
                    options={projects}
                    value={column.projectId}
                    onChange={(value) =>
                      setColumns((prev) =>
                        prev.map((item) =>
                          item.localId === column.localId
                            ? { ...item, projectId: value }
                            : item,
                        ),
                      )
                    }
                  />
                </div>
              )}

              <IconButton
                icon={Trash2}
                label={`Удалить столбец ${String(index + 1)}`}
                variant="danger"
                disabled={columns.length <= 1}
                title={columns.length <= 1 ? "Нужен хотя бы один столбец" : undefined}
                onClick={() =>
                  setColumns((prev) => prev.filter((item) => item.localId !== column.localId))
                }
              />
            </div>
          ))}

          <div>
            <Button
              size="sm"
              variant="secondary"
              icon={Plus}
              onClick={() =>
                setColumns((prev) => [
                  ...prev,
                  {
                    localId: `c${String(Date.now())}`,
                    name: "",
                    projectId: null,
                    statusCategory: columnMode ? null : "OPEN",
                  },
                ])
              }
            >
              Добавить столбец
            </Button>
          </div>
        </fieldset>

        {/* §25.1: участники выбираются при создании и попадают в доску
            с ролью «Участник»; создатель становится руководителем. */}
        {!personal && (
          <MultiSelectPicker
            label="Участники доски"
            placeholder="Выберите участников"
            options={candidates}
            value={memberIds}
            onChange={setMemberIds}
            emptyText={
              directory.isLoading ? "Загрузка сотрудников…" : "Нет доступных сотрудников"
            }
          />
        )}

      </div>
    </Modal>
  );
}
